import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { FunnelIcon } from '@heroicons/react/24/outline'
import MonitorCard from 'components/Dashboard/MonitorCard'
import StatCards from 'components/Dashboard/StatCards'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useTableData } from 'hooks/useTableData'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'

// GET request for packing summary data
const getPackingSummaryData = async (farmId, dateRange) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/packing-summary?${buildDashboardDateRangeQuery(dateRange)}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching packing summary data:', error)
    throw error
  }
}

const themeColorMap = (shade) => {
  return {
    700: 'bg-green-700',
    600: 'bg-green-600',
    500: 'bg-green-500',
    400: 'bg-green-400'
  }[shade]
}

const DailyPackingSummary = () => {
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const filterOptions = useDashboardFilterOptions(user?.farmId)

  // Get current date in Mexico City timezone for date picker
  const currentDateForPicker = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')

  const [dateRange, setDateRange] = useDashboardDateRange(currentDateForPicker)
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)

  // State for API data
  const [packingSummaryData, setPackingSummaryData] = useState({
    dailyPackingSummary: {
      totalPacked: 0,
      packerCount: 0,
      averagePerWorker: 0,
      averagePackedPerHour: 0
    },
    dailyPackingSummarySameHourPreviousDay: {
      totalPacked: 0,
      packerCount: 0
    },
    packerGroupsMonitor: [],
    packersMonitor: [],
    loading: true,
    error: null
  })

  const fetchPackingSummaryData = async (rangeToFetch = dateRange) => {
    if (!user?.farmId) return

    try {
      setPackingSummaryData(prev => ({ ...prev, loading: true, error: null }))

      const data = await getPackingSummaryData(user.farmId, rangeToFetch)

      setPackingSummaryData(prev => ({
        ...prev,
        dailyPackingSummary: data.dailyPackingSummary || prev.dailyPackingSummary,
        dailyPackingSummarySameHourPreviousDay: data.dailyPackingSummarySameHourPreviousDay || prev.dailyPackingSummarySameHourPreviousDay,
        packerGroupsMonitor: data.packerGroupsMonitor || [],
        packersMonitor: data.packersMonitor || [],
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching packing summary:', error)
      setPackingSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load packing summary data'
      }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    fetchPackingSummaryData()
  }, [user?.farmId])

  useEffect(() => {
    if (!hasFetched.current) return
    fetchPackingSummaryData(dateRange)
  }, [dateRange])

  // Create cards data from API response
  const createCardsData = () => {
    const { dailyPackingSummary, dailyPackingSummarySameHourPreviousDay } = packingSummaryData
    const selectedRangeFormatted = getDateRangeSummary(dateRange)

    return [
      {
        title: 'Total Packed',
        value: Number(dailyPackingSummary.totalPacked || 0).toLocaleString(),
        unit: 'Units',
        sub: `Packing Range (${selectedRangeFormatted})`,
        color: 'bg-green-700'
      },
      {
        title: 'Total Packed',
        value: Number(dailyPackingSummarySameHourPreviousDay.totalPacked || 0).toLocaleString(),
        unit: 'Units',
        sub: 'Same Hour On Previous Packing Day',
        color: themeColorMap(500)
      },
      {
        title: 'Packers Count',
        value: Number(dailyPackingSummary.packerCount || 0).toString(),
        unit: 'Workers',
        sub: `Packing Range (${selectedRangeFormatted})`,
        color: 'bg-green-700'
      },
      {
        title: 'Packers Count',
        value: Number(dailyPackingSummarySameHourPreviousDay.packerCount || 0).toString(),
        unit: 'Workers',
        sub: 'Same Hour On Previous Packing Day',
        color: themeColorMap(500)
      },
      {
        title: 'Avg Per Packer',
        value: Number(dailyPackingSummary.averagePerWorker || 0).toFixed(2),
        unit: 'Units',
        sub: `Packing Range (${selectedRangeFormatted})`,
        color: 'bg-green-600'
      },
      {
        title: 'Worker Avg',
        value: Number(dailyPackingSummary.averagePackedPerHour || 0).toFixed(2),
        unit: 'Units/Hour',
        sub: `Packing Range (${selectedRangeFormatted})`,
        color: 'bg-green-700'
      }
    ]
  }

  // Use the custom hook to generate table data with totals
  const packerGroupsTableData = useTableData(packingSummaryData.packerGroupsMonitor, 'packerGroups')
  const packerMonitorTableData = useTableData(packingSummaryData.packersMonitor, 'packerMonitor')

  if (packingSummaryData.loading) {
    return <PageLoadingState message='Loading packing summary...' rows={12} />
  }

  if (packingSummaryData.error) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>{packingSummaryData.error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>

      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-green-600'>Daily Packing Summary</h1>
          <p className='text-sm text-gray-600'>
            Change Filters
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-700'>{getDateRangeSummary(dateRange)}</span>
          <ButtonComponent className='flex items-center gap-2' onClick={() => setShowDateRangeModal(true)}>
            <FunnelIcon className='w-5 h-5' />
            <span>Advanced filters</span>
          </ButtonComponent>
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      <StatCards cards={createCardsData()} />

      <MonitorCard title='Packer Groups Monitor' date={getDateRangeSummary(dateRange)} hasData={packerGroupsTableData.rows.length > 0} color='green'>
        <TableComponent tableData={packerGroupsTableData} defaultSortColumn={packerGroupsTableData.columns[0]?.key} />
      </MonitorCard>

      <MonitorCard title='Packer Monitor' date={getDateRangeSummary(dateRange)} hasData={packerMonitorTableData.rows.length > 0} color='green'>
        <TableComponent tableData={packerMonitorTableData} defaultSortColumn={packerMonitorTableData.columns[0]?.key} />
      </MonitorCard>
      <DateRangeFilterModal
        isOpen={showDateRangeModal}
        dateRange={dateRange}
        sectors={filterOptions.sectors}
        farmingBlocks={filterOptions.farmingBlocks}
        groups={filterOptions.groups}
        activities={filterOptions.activities}
        maxDate={currentDateForPicker}
        onClose={() => setShowDateRangeModal(false)}
        onApply={(nextRange) => {
          setDateRange(nextRange)
          setShowDateRangeModal(false)
        }}
      />
    </div>
  )
}

export default DailyPackingSummary
